// Copyright 2022 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { FrontEnd } from "datatypes/SoramaLocalApi";
import { SorXMetadata, SorXMetadataFull } from "datatypes/SorXFormat";
import { HttpResponseMessageError } from "errors/HttpResponseMessageError";
import { blobToBase64 } from "helpers/SorxHelpers";
import JSZip from "jszip";
import { ProgressIndicator } from "libraries/ProgressIndicator/ProgressIndicator";
import { RequestQueue } from "libraries/RequestQueue/RequestQueue";
import { IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { WebRequestStatus } from "libraries/WebRequestHandler/WebRequestStatus";
import { CamIV64Helpers } from "services/Hardware/CamHTTP/CamIV64Helpers";
import { CamIV64SelectionManager } from "services/Hardware/CamHTTP/CamIV64SelectionManager";
import { FirmwareInfo, ICamApi, PackageFileInfo } from "services/Hardware/CamHTTP/ICamApi";
import { IPrpdDataService, PrpdBinData } from "services/ReportEx/PrpdDataService";
import { ICamIV64File } from "./CamIV64File";

/**
 * Handles HTTP communication with the CamIV64 via the acquisition service
 */
@autoinject
export class CamIV64AcquisitionClientApi implements ICamApi {
    private apiUri: () => Promise<string>;
    private readonly webRequestHandler: IWebRequestHandler;
    private partialDischargeDataService: IPrpdDataService;
    private readonly deviceQueues: Map<string, RequestQueue>;
    private readonly defaultConcurrency: number;

    constructor(
        prpdDataService: IPrpdDataService,
        webRequestHandler: IWebRequestHandler,
        baseUri: () => Promise<string>
    ) {
        this.webRequestHandler = webRequestHandler;
        this.partialDischargeDataService = prpdDataService;
        this.apiUri = baseUri;
        this.defaultConcurrency = 5; // Default concurrency level per device for request queues
        this.deviceQueues = new Map<string, RequestQueue>();
    }

    /**
     * Gets or creates a request queue for the specified device
     */
    private getDeviceQueue(deviceId: string): RequestQueue {
        if (!this.deviceQueues.has(deviceId)) {
            this.deviceQueues.set(deviceId, new RequestQueue(this.defaultConcurrency));
        }
        const queue = this.deviceQueues.get(deviceId);
        if (!queue) {
            throw new Error(`Failed to create or retrieve queue for device: ${deviceId}`);
        }
        return queue;
    }

    /**
     * Queues a request to be executed with concurrency control for the current device
     */
    private queueRequest<T>(requestExecutor: () => Promise<T>): Promise<T> {
        const deviceId = CamIV64SelectionManager.selectedDeviceId;
        if (!deviceId) {
            throw new Error("No device selected");
        }
        const queue = this.getDeviceQueue(deviceId);
        return queue.queueRequest(requestExecutor);
    }

    async canConnect(): Promise<boolean> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            return this.webRequestHandler
                .createGetRequest(
                    (await this.apiUri()) + "/CanConnect?device=" + CamIV64SelectionManager.selectedDeviceId
                )
                .send()
                .then((res) => res.content as boolean);
        });
    }

    async getDeviceId(): Promise<FrontEnd> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            return this.webRequestHandler
                .createGetRequest<FrontEnd>(
                    (await this.apiUri()) + "/Device/Id?device=" + CamIV64SelectionManager.selectedDeviceId
                )
                .send()
                .then((res) => {
                    return {
                        id: res.content.id,
                        connectorCount: 1,
                        microphoneCount: 64,
                        name: res.content.name,
                    } as FrontEnd;
                })
                .catch((e) => {
                    return e.message;
                });
        });
    }

    async getDeviceFirmware(): Promise<FirmwareInfo> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            return await this.webRequestHandler
                .createGetRequest<{ firmwareVersion: string; firmwareInstallDate: string }>(
                    (await this.apiUri()) + "/config/firmware?device=" + CamIV64SelectionManager.selectedDeviceId
                )
                .send()
                .then((res) => {
                    return {
                        version: res.content.firmwareVersion,
                        installDate: new Date(res.content.firmwareInstallDate),
                    } as FirmwareInfo;
                })
                .catch((e) => {
                    throw new Error("Could not get the firmware info from the device");
                });
        });
    }

    async getDeviceConfig(): Promise<unknown> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            return await this.webRequestHandler
                .createGetRequest((await this.apiUri()) + "/config?device=" + CamIV64SelectionManager.selectedDeviceId)
                .send()
                .then((res) => {
                    return res.content;
                });
        });
    }

    async updateDeviceFirmware(
        firmware: Blob,
        progressCallback: (progressIndicator: ProgressIndicator) => void,
        force = false
    ): Promise<void> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            await this.webRequestHandler
                .createPutRequest(
                    (await this.apiUri()) +
                        "/config/firmware?force=" +
                        force.toString() +
                        "&device=" +
                        CamIV64SelectionManager.selectedDeviceId,
                    firmware
                )
                .withUploadProgress(progressCallback)
                .send()
                .catch((res) => {
                    switch (res.statusCode) {
                        case 0:
                            throw new Error("Device not found");
                        case WebRequestStatus.statusNotFound:
                            throw new Error("Firmware update API endpoint not found");
                        default:
                            throw new Error("Could not establish connection with the device");
                    }
                });
        });
    }

    async performFactoryReset(): Promise<void> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            await this.webRequestHandler
                .createPostRequest(
                    (await this.apiUri()) + "/config/factory-reset?device=" + CamIV64SelectionManager.selectedDeviceId
                )
                .send();
        });
    }

    async getMeasurementFileStructure(): Promise<ICamIV64File[]> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            return await this.webRequestHandler
                .createGetRequest(
                    (await this.apiUri()) +
                        "/Measurements/Folder-Structure?withDate=true&device=" +
                        CamIV64SelectionManager.selectedDeviceId
                )
                .send()
                .then((res) => {
                    const responseObject = JSON.parse(res.response);
                    if (Array.isArray(responseObject.FilePaths)) {
                        return responseObject.FilePaths.map((path) => {
                            return { filePath: path, createDate: null } as ICamIV64File;
                        });
                    } else {
                        const files: ICamIV64File[] = [];
                        for (const key in responseObject.FilePaths) {
                            files.push({
                                filePath: key,
                                createDate: new Date(responseObject.FilePaths[key] * 1000),
                            } as ICamIV64File);
                        }
                        return files;
                    }
                })
                .catch((e) => {
                    console.error(e);
                    return [];
                });
        });
    }

    async getMeasurementFileUrl(filePath: string): Promise<string> {
        // URL generation doesn't need queuing as it's not a network request
        this.preRequestCheck();
        const url =
            (await this.apiUri()) +
            "/measurements/files?file=" +
            filePath +
            "&device=" +
            CamIV64SelectionManager.selectedDeviceId;
        return url;
    }

    // Not sure if we need this function?
    async getLogUrl(): Promise<string> {
        // URL generation doesn't need queuing as it's not a network request
        this.preRequestCheck();
        const url = (await this.apiUri()) + "/logs?device=" + CamIV64SelectionManager.selectedDeviceId;
        return url;
    }

    async downloadMeasurementFile(
        filePath: string,
        progressCallback?: (progressIndicator: ProgressIndicator) => void
    ): Promise<Blob> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            return await this.webRequestHandler
                .createGetRequest(
                    (await this.apiUri()) +
                        "/measurements/files?file=" +
                        filePath +
                        "&device=" +
                        CamIV64SelectionManager.selectedDeviceId
                )
                .withDownloadProgress(progressCallback)
                .withBinaryResponse()
                .send()
                .then((res) => {
                    return res.content as Blob;
                })
                .catch((reason: HttpResponseMessageError) => {
                    const message: string = reason.message ? reason.message : "Could not download measurement file";
                    const err = new Error(message);
                    throw err;
                });
        });
    }

    async getSpecialMeasurementFileStructure(): Promise<ICamIV64File[]> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            return await this.webRequestHandler
                .createGetRequest(
                    (await this.apiUri()) +
                        "/Special-Measurements/Folder-Structure?withDate=true&device=" +
                        CamIV64SelectionManager.selectedDeviceId
                )
                .send()
                .then((res) => {
                    const responseObject = JSON.parse(res.response);
                    if (Array.isArray(responseObject.FilePaths)) {
                        return responseObject.FilePaths.map((path) => {
                            return { filePath: path, createDate: null } as ICamIV64File;
                        });
                    } else {
                        const files: ICamIV64File[] = [];
                        for (const key in responseObject.FilePaths) {
                            files.push({
                                filePath: key,
                                createDate: new Date(responseObject.FilePaths[key] * 1000),
                            } as ICamIV64File);
                        }
                        return files;
                    }
                })
                .catch((e) => {
                    console.error(e);
                    return [];
                });
        });
    }

    async getCAMiV64Files(): Promise<ICamIV64File[]> {
        // This method orchestrates multiple requests, but each individual request is already queued
        this.preRequestCheck();
        const deviceType = CamIV64Helpers.getDeviceType(CamIV64SelectionManager.selectedDeviceId);
        if (deviceType === "iV64Ex" || deviceType === "iV64s") {
            const sorXFiles = await this.getSorXMeasurementFileStructure();
            return sorXFiles;
        }

        // iV64 devices with firmware that we will release soon support SorX measurements
        if (deviceType === "iV64") {
            const firmware = await this.getDeviceFirmware();
            const sorXSupported = CamIV64Helpers.supportsSorX(deviceType, firmware.version);

            if (sorXSupported === true) {
                return await this.getSorXMeasurementFileStructure();
            }

            // Dev firmware where version cannot be parsed — attempt SorX, fall back to old endpoints
            if (sorXSupported === null) {
                try {
                    return await this.getSorXMeasurementFileStructure();
                } catch {
                    // Fall through to legacy endpoints
                }
            }
        }

        const [specialMeasurementFiles, measurementFiles] = await Promise.resolve([
            this.getSpecialMeasurementFileStructure(),
            this.getMeasurementFileStructure(),
        ]);
        const files = [...(await specialMeasurementFiles), ...(await measurementFiles)];
        return files;
    }

    async getSpecialMeasurementFileUrl(filePath: string): Promise<string> {
        this.preRequestCheck();
        const url =
            (await this.apiUri()) +
            "/special-measurements/files?file=" +
            filePath +
            "&device=" +
            CamIV64SelectionManager.selectedDeviceId;
        return url;
    }

    async downloadSpecialMeasurementFile(
        filePath: string,
        progressCallback?: (progressIndicator: ProgressIndicator) => void
    ): Promise<object> {
        return this.queueRequest<object>(async () => {
            const downloadProgressCallback = progressCallback ?? ((progress) => {});

            this.preRequestCheck();
            return await this.webRequestHandler
                .createGetRequest(
                    (await this.apiUri()) +
                        "/special-measurements/files?file=" +
                        filePath +
                        "&device=" +
                        CamIV64SelectionManager.selectedDeviceId
                )
                .withDownloadProgress(downloadProgressCallback)
                .send()
                .then((res) => res.content as object)
                .catch((reason: HttpResponseMessageError) => {
                    const message: string = reason.message ? reason.message : "Could not download measurement file";
                    const err = new Error(message);
                    throw err;
                });
        });
    }

    async getSorXMeasurementFileStructure(): Promise<ICamIV64File[]> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            return await this.webRequestHandler
                .createGetRequest(
                    (await this.apiUri()) + "/measurements/sorXList?device=" + CamIV64SelectionManager.selectedDeviceId
                )
                .send()
                .then((res) => {
                    const responseObject = JSON.parse(res.response) as string[];
                    return responseObject.map((path) => {
                        return { filePath: path, createDate: null } as ICamIV64File;
                    });
                });
        });
    }
    async getFavoriteMeasurements(): Promise<ICamIV64File[]> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            return await this.webRequestHandler
                .createGetRequest(
                    (await this.apiUri()) + "/measurements/favorites?device=" + CamIV64SelectionManager.selectedDeviceId
                )
                .send()
                .then((res) => {
                    const responseObject = JSON.parse(res.response) as string[];
                    return responseObject.map((path) => {
                        return { filePath: path, createDate: null } as ICamIV64File;
                    });
                })
                .catch((e) => {
                    console.error(e);
                    return [];
                });
        });
    }

    async downloadSorXMeasurementMetadata(
        filePath: string,
        progressCallback?: (progressIndicator: ProgressIndicator) => void
    ): Promise<SorXMetadataFull> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            const downloadProgressCallback = progressCallback ?? ((progress) => {});

            filePath = filePath.startsWith("/") ? filePath.substring(1) : filePath;

            const sorXBytes = await this.webRequestHandler
                .createGetRequest(
                    (await this.apiUri()) +
                        "/measurements/sorX?measurement=" +
                        filePath +
                        "&device=" +
                        CamIV64SelectionManager.selectedDeviceId
                )
                .withDownloadProgress(downloadProgressCallback)
                .withBinaryResponse()
                .send()
                .then((res) => res.content)
                .catch((reason: HttpResponseMessageError) => {
                    const message: string = reason.message ? reason.message : "Could not download measurement file";
                    const err = new Error(message);
                    throw err;
                });

            const sorX = await JSZip.loadAsync(sorXBytes);
            const metadata: SorXMetadata = JSON.parse(await sorX.file("metadata.json").async("string"));
            const imageBase64: string = (await blobToBase64(
                new Blob([await sorX.file("image.jpeg").async("blob")], { type: "image/jpeg" })
            )) as string;

            const prpdFile = sorX.file("PrpdData.bin");
            let prpdData: PrpdBinData = {
                dischargeDataPoints: [],
                pulseCount: 0,
            };
            if (prpdFile) {
                const prpdArrayBuffer = await prpdFile.async("arraybuffer");
                prpdData = this.partialDischargeDataService.parsePrpdBin(prpdArrayBuffer);
            } else {
                console.warn("PrpdData.bin not found in the measurement zip.");
            }

            // Adds the feature images to the metadata if they exist
            const featureImages: string[] = [];
            const featureImagesCount = 3;
            for (let i = 1; i <= featureImagesCount; i++) {
                const file = sorX.file(`notes/${i}.jpeg`);
                if (file) {
                    const blob = await file.async("blob");
                    const base64 = await blobToBase64(new Blob([blob], { type: "image/jpeg" }));
                    featureImages.push(base64 as string);
                }
            }

            const dto: SorXMetadataFull = {
                ...metadata,
                image: imageBase64,
                featureImages: featureImages,
                prpdBinData: prpdData,
            };
            return dto;
        });
    }

    async downloadSorXMeasurementFile(
        filePath: string,
        progressCallback?: (progressIndicator: ProgressIndicator) => void
    ): Promise<Blob> {
        return this.queueRequest(async () => {
            const downloadProgressCallback = progressCallback ?? ((progress) => {});

            this.preRequestCheck();
            filePath = filePath.startsWith("/") ? filePath.substring(1) : filePath;

            return await this.webRequestHandler
                .createGetRequest(
                    (await this.apiUri()) +
                        "/measurements/sorX?measurement=" +
                        filePath +
                        "&device=" +
                        CamIV64SelectionManager.selectedDeviceId
                )
                .withDownloadProgress(downloadProgressCallback)
                .withBinaryResponse()
                .send()
                .then((res) => res.content)
                .catch((reason: HttpResponseMessageError) => {
                    const message: string = reason.message ? reason.message : "Could not download measurement file";
                    const err = new Error(message);
                    throw err;
                });
        });
    }

    async deleteFileOrFolder(filePath: string): Promise<void> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            await this.webRequestHandler
                .createDeleteRequest(
                    (await this.apiUri()) +
                        "/measurements/files?file=" +
                        filePath +
                        "&device=" +
                        CamIV64SelectionManager.selectedDeviceId
                )
                .send()
                .then((res) => {
                    return res.content;
                })
                .catch((reason: HttpResponseMessageError) => {
                    const message: string = reason.message ? reason.message : "Could not delete files";
                    const err = new Error(message);
                    throw err;
                });
        });
    }

    async getHardwareClock(): Promise<number> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            return await this.webRequestHandler
                .createGetRequest(
                    (await this.apiUri()) + "/config/hardwareclock?device=" + CamIV64SelectionManager.selectedDeviceId
                )
                .send()
                .then((res) => {
                    return JSON.parse(res.response);
                })
                .catch((e) => {
                    console.error(e);
                    return;
                });
        });
    }

    async getLicenseTagsOnDevice(): Promise<string[]> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            return await this.webRequestHandler
                .createGetRequest((await this.apiUri()) + "/license?device=" + CamIV64SelectionManager.selectedDeviceId)
                .send()
                .then((res) => {
                    if (!res.response || res.response == "") {
                        return [];
                    }
                    return JSON.parse(res.response);
                })
                .catch((e) => {
                    console.error(e);
                    return;
                });
        });
    }

    async sendLicenseToDevice(encodedLicensePayload: string): Promise<void> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            return this.webRequestHandler
                .createPutRequest<void>(
                    (await this.apiUri()) + "/license?device=" + CamIV64SelectionManager.selectedDeviceId,
                    encodedLicensePayload
                )
                .send()
                .then((res) => res.content)
                .catch((e) => {
                    // If you are in develop, check if the private-key in the web.config backend is correct.
                    throw new Error("Could not place the license on the device. Please contact Sorama.");
                });
        });
    }

    async sendPackageFileToDevice(encodedPackageFilePayload: PackageFileInfo): Promise<void> {
        return this.queueRequest(async () => {
            this.preRequestCheck();
            return this.webRequestHandler
                .createPutRequest<void>(
                    (await this.apiUri()) + "/package?device=" + CamIV64SelectionManager.selectedDeviceId,
                    encodedPackageFilePayload
                )
                .send()
                .then((response) => response.content)
                .catch((e) => {
                    console.error("Could not place package file on the device.");
                    return;
                });
        });
    }

    private preRequestCheck() {
        if (!CamIV64SelectionManager.isDeviceSelected) {
            throw new Error("No device selected");
        }
    }
}
